import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, MapPin, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AREAS, OTHER_AREAS, PROJECTS, SERVICES, TESTIMONIALS } from "@/data/site";
import { Overline, Reveal } from "@/components/Reveal";

export const ServicesGrid = ({ heading = "Remodeling Services in Huntsville, AL" }) => (
  <section className="bg-cream py-16 md:py-24" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Overline>What We Build</Overline>
        <h2 className="mt-3 max-w-2xl font-serif text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
          {heading}
        </h2>
      </Reveal>
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={i * 0.1}>
            <Link
              to={`/${s.slug}`}
              data-testid={`service-card-${s.slug}`}
              className="group block h-full overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={s.image}
                  alt={s.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 rounded-full bg-navy/85 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white backdrop-blur-sm">
                  {s.short}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl font-semibold text-navy sm:text-2xl">{s.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.description}</p>
                <ul className="mt-4 space-y-1.5">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm font-medium text-navy">
                      <Check className="h-4 w-4 shrink-0 text-salmon" strokeWidth={3} /> {f}
                    </li>
                  ))}
                </ul>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-salmon transition-colors group-hover:text-salmon-dark">
                  Explore {s.short} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FILTERS = ["All", "Bathrooms", "Kitchens", "Flooring"];

export const GallerySection = ({ heading = "Recent Work Around Huntsville", sub }) => {
  const [filter, setFilter] = useState("All");
  const shown = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <section className="bg-white py-16 md:py-24" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Overline>Our Portfolio</Overline>
            <h2 className="mt-3 font-serif text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              {heading}
            </h2>
            {sub && <p className="mt-2 max-w-lg text-sm leading-relaxed text-slate-600">{sub}</p>}
          </div>
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter projects">
            {FILTERS.map((f) => (
              <button
                key={f}
                role="tab"
                aria-selected={filter === f}
                data-testid={`gallery-filter-${f.toLowerCase()}`}
                onClick={() => setFilter(f)}
                className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                  filter === f
                    ? "bg-navy text-white shadow-md"
                    : "bg-cream text-navy hover:bg-salmon-light hover:text-salmon-dark"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <figure
                className="group relative aspect-[3/4] overflow-hidden rounded-2xl"
                data-testid={`gallery-card-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <img
                  src={p.image}
                  alt={p.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy-deep/95 via-navy-deep/50 to-transparent p-5 pt-16">
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-salmon-vibrant">
                    {p.category}
                  </span>
                  <p className="mt-1 font-serif text-xl font-semibold text-white">{p.title}</p>
                  {p.city && (
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-white/70">
                      <MapPin className="h-3 w-3" /> {p.city}, AL
                    </p>
                  )}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Testimonials = () => (
  <section className="bg-cream py-16 md:py-24" data-testid="testimonials-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Overline>Word of Mouth</Overline>
        <h2 className="mt-3 font-serif text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
          What Your Neighbors Say
        </h2>
      </Reveal>
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.08}>
            <blockquote className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="flex gap-0.5" aria-label="5 out of 5 stars">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">"{t.quote}"</p>
              <footer className="mt-5 border-t border-slate-100 pt-4">
                <p className="text-sm font-bold text-navy">{t.name}</p>
                <p className="text-xs text-slate-500">{t.project} · {t.city}, AL</p>
              </footer>
            </blockquote>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const FAQSection = ({ faqs, heading = "Frequently Asked Questions" }) => (
  <section className="bg-white py-16 md:py-24" data-testid="faq-section">
    <div className="mx-auto max-w-3xl px-6">
      <Reveal className="text-center">
        <Overline>Good to Know</Overline>
        <h2 className="mt-3 font-serif text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
          {heading}
        </h2>
      </Reveal>
      <Reveal delay={0.1} className="mt-10">
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              data-testid={`faq-accordion-item-${i + 1}`}
              className="border-slate-200"
            >
              <AccordionTrigger className="text-left font-sans text-base font-semibold text-navy hover:text-salmon hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-slate-600">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const AreaChips = ({ heading = "Proudly Serving North Alabama" }) => (
  <section className="bg-navy py-16 md:py-20" data-testid="areas-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Overline light>Service Areas</Overline>
        <h2 className="mt-3 font-serif text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
          {heading}
        </h2>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/70">
          Based in Huntsville and serving Madison, Limestone and Morgan counties. Tap your city for local project details.
        </p>
      </Reveal>
      <Reveal delay={0.1} className="mt-8 flex flex-wrap gap-3">
        {AREAS.map((a) => (
          <Link
            key={a.slug}
            to={`/service-areas/${a.slug}`}
            data-testid={`area-chip-${a.slug}`}
            className="group flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white transition-all hover:border-salmon hover:bg-salmon"
          >
            {a.name}, AL
            <ArrowUpRight className="h-4 w-4 text-salmon-vibrant transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
          </Link>
        ))}
        {OTHER_AREAS.map((c) => (
          <span
            key={c}
            className="rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-white/60"
          >
            {c}
          </span>
        ))}
      </Reveal>
    </div>
  </section>
);
